import { Button } from '@components/Button'
import { HeroIcon } from '@components/HeroIcon'
import { ReactNode } from 'react'
import {
  Adapt,
  Dialog,
  RadiusTokens,
  Sheet,
  SheetProps,
  Unspaced,
  XStack,
  useMedia,
  AdaptWhen,
} from 'tamagui'

interface IModalProps extends SheetProps {
  onClose?: () => void
  children: ReactNode
  maxWidth?: number
  shouldAdapt?: boolean
  whenShouldAdapt?: AdaptWhen
  open: boolean
  backdropOpacity?: number
  snapPoints?: number[]
  snapPointsMode?: 'percent' | 'fit' | 'constant' | 'mixed'
  dismissOnOverlayPress?: boolean
  borderRadius?: RadiusTokens
  keepVisible?: boolean
  hideOverlay?: boolean
  zIndex?: number
  withBorder?: boolean
}
export interface IModalHandles {
  showModal: (show: boolean) => void
}

export function Modal({
  open,
  snapPoints,
  snapPointsMode = 'percent',
  shouldAdapt = true,
  whenShouldAdapt = 'sm',
  maxWidth,
  onClose,
  children,
  backdropOpacity = 0.1,
  dismissOnOverlayPress = true,
  borderRadius = '$border-radius-sm',
  keepVisible = false,
  hideOverlay = false,
  zIndex = 9999999,
  withBorder = false,
  ...rest
}: IModalProps) {
  function handleClose() {
    onClose && onClose()
  }
  const media = useMedia()
  const closeButtonTop = media.gtSm ? 25 : 0
  const portalWidth = hideOverlay ? '0%' : '100%'

  return (
    <Dialog modal open={open}>
      <Adapt when={whenShouldAdapt} platform="touch">
        <Sheet
          open={open}
          animation="medium"
          zIndex={zIndex}
          modal
          onOpenChange={onClose}
          dismissOnOverlayPress={dismissOnOverlayPress}
          dismissOnSnapToBottom={!keepVisible}
          snapPoints={snapPoints}
          snapPointsMode={snapPointsMode}
          moveOnKeyboardChange
          {...rest}
        >
          {!hideOverlay && (
            <Sheet.Overlay
              testID="modal-overlay"
              opacity={backdropOpacity}
              enterStyle={{ opacity: 0 }}
              exitStyle={{ opacity: 0 }}
              backgroundColor="$bg-overlay"
              onPress={handleClose}
            />
          )}
          <Sheet.Frame
            testID="modal-content"
            bg="$bg-surface"
            br={borderRadius}
            paddingTop={!shouldAdapt ? 10 : 0}
            borderTopWidth={withBorder ? 2 : 0}
            borderLeftWidth={withBorder ? 2 : 0}
            borderRightWidth={withBorder ? 2 : 0}
            borderColor={withBorder ? '$border-subtle' : 'transparent'}
            width={withBorder ? '99%' : '100%'}
          >
            {shouldAdapt && (
              <XStack
                testID="modal-drag-indicator"
                width={80}
                height={8}
                bg="$control-inactive"
                br="$border-radius-pill"
                alignSelf="center"
                marginTop="$size.spacing-xs"
              />
            )}
            <Adapt.Contents />
          </Sheet.Frame>
        </Sheet>
      </Adapt>

      <Dialog.Portal width={portalWidth}>
        {!hideOverlay && (
          <Dialog.Overlay
            testID="modal-overlay"
            key="overlay"
            animation="quick"
            opacity={backdropOpacity}
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
            backgroundColor="$bg-overlay"
            onPress={handleClose}
          />
        )}

        <Dialog.Content
          testID="modal-content"
          key="content"
          animateOnly={['transform', 'opacity']}
          animation={[
            'quick',
            {
              opacity: {
                overshootClamping: true,
              },
            },
          ]}
          enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
          exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
          width="100%"
          maxWidth={maxWidth || 600}
          unstyled
          backgroundColor="$bg-surface"
          zIndex={zIndex}
          br={borderRadius}
        >
          {children}

          <Unspaced>
            <Dialog.Close asChild>
              <Button
                testID="modal-close-button"
                position="absolute"
                top={closeButtonTop}
                right={10}
                onPress={handleClose}
                icon
                outlineWidth={0}
                pressStyle={{ borderWidth: 0 }}
                focusStyle={{ borderWidth: 2, borderColor: 'transparent' }}
              >
                <HeroIcon
                  icon={['fas', 'xmark']}
                  size={20}
                  color="$icon-primary"
                />
              </Button>
            </Dialog.Close>
          </Unspaced>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  )
}
